import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import './App.css';

// Sideo - Staging and Production Workflow
// Auto-detects backend URL based on deployment environment

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import ScannerPage from './pages/ScannerPage';
import ChatPage from './pages/ChatPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminAuditLogs from './pages/admin/AdminAuditLogs';
import AdminSettings from './pages/admin/AdminSettings';
import AdminUsers from './pages/admin/AdminUsers';
import AdminQRCodes from './pages/admin/AdminQRCodes';
import AdminMessages from './pages/admin/AdminMessages';
import AdminCalls from './pages/admin/AdminCalls';
import AdminQROrders from './pages/admin/AdminQROrders';

// QR Pool Pages
import QRScanPage from './pages/QRScanPage';
import ClaimQRPage from './pages/ClaimQRPage';
import RenewalPage from './pages/RenewalPage';
import QRPoolDashboard from './pages/admin/QRPoolDashboard';
import BuyQRCodePage from './pages/BuyQRCodePage';
import BuyQRCodeProcessingPage from './pages/BuyQRCodeProcessingPage';
import BuyQRCodeSuccessPage from './pages/BuyQRCodeSuccessPage';
import ActivateQRPage from './pages/ActivateQRPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundReplacementPolicyPage from './pages/RefundReplacementPolicyPage';

// Determine backend URL based on environment
const getBackendURL = () => {
  // Detect by hostname first (avoids shared Vercel env vars affecting previews)
  const hostname = window.location.hostname;

  // Production domains
  if (
    hostname === 'app.sideo.in' ||
    hostname === 'sideo-pre-qr.vercel.app' ||
    hostname === 'sideo.in' ||
    hostname === 'www.sideo.in'
  ) {
    return 'https://sideo-pre-qr-production.up.railway.app';
  }

  // Local development can still use explicit env vars if provided
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
  }

  // All other deployments (preview, staging, etc.) use staging backend
  return 'https://sideo-staging-pre-qr-production.up.railway.app';
};



const RAW_BACKEND_URL = getBackendURL();
export const API = RAW_BACKEND_URL?.endsWith('/api') ? RAW_BACKEND_URL : `${RAW_BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  if (!token) {
    const nextPath = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/refund-replacement-policy" element={<RefundReplacementPolicyPage />} />
          <Route path="/activate-qr" element={<ActivateQRPage />} />
          <Route path="/login" element={<LoginPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buy-qr"
            element={
              <ProtectedRoute>
                <BuyQRCodePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buy-qr/processing"
            element={
              <ProtectedRoute>
                <BuyQRCodeProcessingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buy-qr/success"
            element={
              <ProtectedRoute>
                <BuyQRCodeSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route path="/scan/:qrCode" element={<ScannerPage />} />
          <Route path="/chat/:sessionId?" element={<ChatPage />} />
          
          {/* QR Pool Routes */}
          <Route path="/q/:qrId" element={<QRScanPage />} />
          <Route path="/q/:qrId/claim" element={<ClaimQRPage />} />
          <Route path="/q/:qrId/renew" element={<RenewalPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin setIsAuthenticated={setIsAuthenticated} />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/qr-codes"
            element={
              <ProtectedRoute>
                <AdminQRCodes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/qr-pool"
            element={
              <ProtectedRoute>
                <QRPoolDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute>
                <AdminNotifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <ProtectedRoute>
                <AdminMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/calls"
            element={
              <ProtectedRoute>
                <AdminCalls />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/qr-orders"
            element={
              <ProtectedRoute>
                <AdminQROrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute>
                <AdminAuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
